import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTeacherProfiles } from '@/hooks/useTeacherProfiles';
import { useTeacherProfilesFilters } from '@/hooks/useTeacherProfilesFilters';
import { TeacherProfilesPageHeader } from '@/components/admin/teacher-profiles/TeacherProfilesPageHeader';
import { TeacherProfilesControlPanel } from '@/components/admin/teacher-profiles/TeacherProfilesControlPanel';
import { TeacherProfilesResults } from '@/components/admin/teacher-profiles/TeacherProfilesResults';
import { Card, CardContent } from '@/components/ui/card';
import { AdminSidebar } from '@/components/admin/AdminSidebar';
import { SidebarProvider } from '@/components/ui/sidebar';

const ITEMS_PER_PAGE = 20;

interface AvailabilityFilter {
  day: string;
  startTime: string;
  endTime: string;
}

const AdminTeacherProfilesPage: React.FC = () => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [availabilityFilter, setAvailabilityFilter] = useState<AvailabilityFilter | null>(null);

  // Fetch all data at once
  const { data: allData, isLoading, error } = useTeacherProfiles(includeInactive);

  // Apply filters to the data
  const {
    filters,
    filteredProfiles,
    filterOptions,
    updateFilter,
    clearFilters,
    hasActiveFilters
  } = useTeacherProfilesFilters(allData?.data || []);

  // Function to check if a teacher is available during the specified time
  const isTeacherAvailable = (profile: any, filter: AvailabilityFilter) => {
    const { day, startTime, endTime } = filter;
    const dayOfWeek = parseInt(day);

    console.log(profile, filter);
    
    // Check if teacher has availability templates for this day
    const dayTemplates = profile.availability_templates?.filter(
      (template: any) => template.day_of_week === dayOfWeek
    ) || [];

    if (dayTemplates.length === 0) {
      return false; // Teacher is not available on this day
    }

    // Check if any time slot overlaps with the requested time
    return dayTemplates.some((template: any) => {
      const templateStart = template.start_time;
      const templateEnd = template.end_time;
      
      // Check if the requested time overlaps with the template time
      // Requested time should be within or overlap with template time
      return (
        (startTime >= templateStart && startTime < templateEnd) || // Requested start is within template
        (endTime > templateStart && endTime <= templateEnd) || // Requested end is within template
        (startTime <= templateStart && endTime >= templateEnd) // Requested time completely covers template
      );
    });
  };

  // Client-side search, pagination, and availability filtering on filtered data
  const searchedAndPaginatedData = useMemo(() => {
    // Apply search to filtered data
    let searchedData = filteredProfiles;
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      searchedData = filteredProfiles.filter(profile => 
        profile.teacher_code.toLowerCase().includes(query) ||
        profile.full_name.toLowerCase().includes(query) ||
        (profile.nickname && profile.nickname.toLowerCase().includes(query))
      );
    }

    // Apply availability filter
    if (availabilityFilter) {
      searchedData = searchedData.filter(profile => 
        isTeacherAvailable(profile, availabilityFilter)
      );
    }

    // Calculate pagination
    const totalCount = searchedData.length;
    const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const paginatedData = searchedData.slice(startIndex, endIndex);

    return {
      data: paginatedData,
      totalCount,
      totalPages
    };
  }, [filteredProfiles, searchQuery, currentPage, availabilityFilter]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleFilterChange = (filterType: 'subject' | 'gradeLevel' | 'courseType' | 'specialized', value: string) => {
    updateFilter(filterType, value);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handleClearFilters = () => {
    clearFilters();
    setCurrentPage(1);
  };

  const handleToggleChange = (newIncludeInactive: boolean) => {
    setIncludeInactive(newIncludeInactive);
    setCurrentPage(1); // Reset to first page when toggling
  };

  const handleAvailabilityFilter = (day: string, startTime: string, endTime: string) => {
    
    // Validate time range
    if (startTime >= endTime) {
      alert('เวลาเริ่มต้นต้องน้อยกว่าเวลาสิ้นสุด');
      return;
    }

    setAvailabilityFilter({ day, startTime, endTime });
    setCurrentPage(1); // Reset to first page when applying availability filter
  };

  const handleClearAvailabilityFilter = () => {
    setAvailabilityFilter(null);
    setCurrentPage(1);
  };

  const handleBackToAdmin = () => {
    navigate('/admin');
  };

  const wrapWithSidebar = (content: React.ReactNode) => (
    <SidebarProvider>
      <div className="flex min-h-screen w-full">
        <AdminSidebar />
        <main className="flex-1 overflow-auto">
          {content}
        </main>
      </div>
    </SidebarProvider>
  );

  if (error) {
    return wrapWithSidebar(
      <div className="container mx-auto px-4 py-8">
        <Card>
          <CardContent className="py-8">
            <div className="text-center text-red-600">
              เกิดข้อผิดพลาดในการโหลดข้อมูล: {error.message}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return wrapWithSidebar(
    <div className="container mx-auto px-4 py-8">
      <div className="space-y-6">
        <TeacherProfilesPageHeader onBackToAdmin={handleBackToAdmin} />

        <TeacherProfilesControlPanel
          includeInactive={includeInactive}
          onToggleChange={handleToggleChange}
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          filters={filters}
          filterOptions={filterOptions}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
          hasActiveFilters={hasActiveFilters}
          onAvailabilityFilter={handleAvailabilityFilter}
          onClearAvailabilityFilter={handleClearAvailabilityFilter}
          currentAvailabilityFilter={availabilityFilter}
        />

        <TeacherProfilesResults
          profiles={searchedAndPaginatedData.data}
          isLoading={isLoading}
          totalCount={searchedAndPaginatedData.totalCount}
          includeInactive={includeInactive}
          currentPage={currentPage}
          totalPages={searchedAndPaginatedData.totalPages}
          onPageChange={handlePageChange}
        />
      </div>
    </div>
  );
};

export default AdminTeacherProfilesPage;
